import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Separator } from "@/components/ui/separator";
import { ArrowRightLeft } from "lucide-react";
import api, { handleApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

export default function CompanyHistory() {
  const [anticipations, setAnticipations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/anticipations?page=${page}&page_size=20`);
      const data = res.data;
      setAnticipations(data.items || data);
      setPagination(data.pagination || null);
    } catch (err) { toast.error(handleApiError(err, "Erro ao carregar histórico")); }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-screen"><div className="loading-spinner w-8 h-8" /></div>
  );

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-5 lg:space-y-6" data-testid="company-history">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Histórico de Antecipações</h1>
        <p className="text-sm text-muted-foreground mt-1">{pagination?.total || anticipations.length} registro{(pagination?.total || anticipations.length) !== 1 ? "s" : ""}</p>
      </div>

      {anticipations.length === 0 ? (
        <Card className="border-white/5" data-testid="history-empty-state">
          <CardContent className="py-12 text-center">
            <ArrowRightLeft className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nenhuma antecipação registrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 stagger-children">
          {anticipations.map((a) => (
            <Card key={a.id} data-testid={`history-item-${a.id}`} className="border-white/5 animate-fade-in card-interactive">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium">{a.employee_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(a.created_at)}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <Separator className="bg-white/5 my-3" />
                <div className="flex justify-between text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Bruto</p>
                    <p className="font-medium">{formatCurrency(a.total_bruto)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-0.5">Títulos</p>
                    <p className="font-medium">{a.titulo_count}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-0.5">Líquido</p>
                    <p className="font-semibold text-emerald-400">{formatCurrency(a.total_liquido)}</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">ID: {a.external_id}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages}
        total={pagination.total} pageSize={pagination.page_size} onPageChange={setPage} />}
    </div>
  );
}
